/**
 * Cache Freshness Check
 * 
 * Ensures users always get the latest version of the app.
 * Runs on page load to detect stale cached content.
 * 
 * How it works:
 * 1. Fetches the current manifest.json from the server (no-cache)
 * 2. Compares the server version with the app's built-in version
 * 3. If they don't match, clears caches and forces reload
 */

// Get build version (injected at build time)
declare const __APP_VERSION__: string;
declare const __BUILD_TIME__: string;
declare const __DEPLOY_VERSION__: string;

interface ManifestData {
    name: string;
    version?: string;
    build_time?: string;
}

/**
 * Check if the current app version matches the server version
 * Returns true if cache is fresh, false if stale
 */
export async function checkCacheFreshness(): Promise<boolean> {
    try {
        // Skip check in development
        if (import.meta.env.DEV) {
            return true;
        }
        
        // Check if we've already verified freshness recently (within 30 seconds)
        const lastCheck = sessionStorage.getItem('cache_freshness_check');
        if (lastCheck && Date.now() - parseInt(lastCheck) < 30000) {
            return true;
        }

        // Fetch manifest with aggressive no-cache headers
        const response = await fetch('/manifest.json', {
            method: 'GET',
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
            }
        });

        if (!response.ok) {
            console.warn('[CacheCheck] Failed to fetch manifest:', response.status);
            return true; // Assume fresh if can't check
        }

        // Also check the HTML page for version meta tag
        const htmlVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
        
        // Get current build info
        let currentVersion = '0.0.0';
        let currentBuildTime = '';
        
        try {
            currentVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
            currentBuildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';
        } catch (e) {
            // Build constants not available
        }

        // Parse response
        const url = new URL(response.url);
        const serverTimestamp = url.searchParams.get('t') || url.searchParams.get('v');
        
        // Also check ETag or Last-Modified headers
        const serverETag = response.headers.get('ETag');
        const lastModified = response.headers.get('Last-Modified');

        // Store check timestamp
        sessionStorage.setItem('cache_freshness_check', Date.now().toString());

        // If HTML version doesn't match built version, we're stale
        if (htmlVersion && htmlVersion !== currentVersion) {
            console.log('[CacheCheck] Version mismatch detected!', {
                html: htmlVersion,
                built: currentVersion
            });
            return false;
        }

        // Check stored version against current
        const storedVersion = localStorage.getItem('app_deployed_version');
        const storedBuildTime = localStorage.getItem('app_build_time');
        
        if (storedVersion && storedVersion !== currentVersion) {
            console.log('[CacheCheck] Stored version mismatch!', {
                stored: storedVersion,
                current: currentVersion
            });
            
            // Update stored values
            localStorage.setItem('app_deployed_version', currentVersion);
            localStorage.setItem('app_build_time', currentBuildTime);
            
            return false;
        }
        
        // First visit - store version
        if (!storedVersion) {
            localStorage.setItem('app_deployed_version', currentVersion);
            localStorage.setItem('app_build_time', currentBuildTime);
        }

        return true;
    } catch (error) {
        console.error('[CacheCheck] Error checking freshness:', error);
        return true; // Assume fresh on error
    }
}

/**
 * Force clear all caches and reload with fresh content
 */
export async function forceFreshReload(): Promise<void> {
    console.log('[CacheCheck] Forcing fresh reload...');
    
    try {
        // 1. Clear all browser caches (Cache API)
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            console.log('[CacheCheck] Cleared Cache API:', cacheNames.length, 'caches');
        }
        
        // 2. Unregister all service workers
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
            }
            console.log('[CacheCheck] Unregistered', registrations.length, 'service workers');
        }
        
        // 3. Clear sessionStorage cache markers
        sessionStorage.removeItem('cache_freshness_check');
        
        // 4. Update version tracking
        try {
            const currentVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
            localStorage.setItem('app_deployed_version', currentVersion);
            localStorage.setItem('app_last_cache_clear', new Date().toISOString());
        } catch (e) {
            // Ignore
        }
        
        // 5. Force hard reload with cache bypass
        // Using a timestamp query param ensures no HTTP cache hit
        const freshUrl = new URL(window.location.href);
        freshUrl.searchParams.set('_t', Date.now().toString());
        freshUrl.searchParams.set('_fresh', '1');
        
        console.log('[CacheCheck] Reloading to:', freshUrl.toString());
        window.location.replace(freshUrl.toString());
    } catch (error) {
        console.error('[CacheCheck] Force reload failed:', error);
        // Fallback: simple reload
        window.location.reload();
    }
}

/**
 * Initialize cache freshness monitoring
 * Call this in main.tsx or App.tsx
 */
export async function initCacheMonitoring(): Promise<void> {
    // Skip if we just did a fresh reload
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('_fresh')) {
        // Clean up the URL
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('_t');
        cleanUrl.searchParams.delete('_fresh');
        window.history.replaceState({}, '', cleanUrl.toString());
        
        console.log('[CacheCheck] Fresh load completed');
        return;
    }
    
    // Check freshness
    const isFresh = await checkCacheFreshness();
    
    if (!isFresh) {
        console.log('[CacheCheck] Stale content detected, forcing refresh...');
        await forceFreshReload();
    } else {
        console.log('[CacheCheck] Cache is fresh ✓');
    }
}

export default {
    checkCacheFreshness,
    forceFreshReload,
    initCacheMonitoring
};

